import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools } from "zustand/middleware";
import { current } from "immer";
import type { OfferDetailResponse } from "@codway-dev/shared";
import { OfferService } from "../services/offer.service";
import { isDeepEqual } from "../utils/deep-equal";
import {
  DEFAULT_EDITOR_STATE,
  offerToState,
  stateToCreateRequest,
  stateToUpdateRequest,
  type OneClickEditorState,
} from "./one-click-editor.mapping";

// ─── Types ────────────────────────────────────────────────────────────────────

interface OneClickEditorStoreState {
  config: OneClickEditorState;
  offerId: string | null;
  originalConfig: OfferDetailResponse["config"] | null;
  // Captured at load/save time so the dirty check has a stable baseline
  // to compare against. Held by reference — Immer auto-freezes nested
  // objects in produced state, so subsequent edits draft a fresh copy
  // instead of mutating this snapshot.
  initialConfig: OneClickEditorState | null;
  /**
   * The `is_active` value currently persisted on the backend for this offer.
   * For a new draft (no offerId) this is `false` since the offer doesn't exist
   * in the active count yet. Used by the editor to decide whether activating
   * the draft would cross the modal-active limit.
   */
  initialIsActive: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

interface OneClickEditorActions {
  initNew: () => void;
  loadFromBackend: (id: string) => Promise<void>;
  updateField: <K extends keyof OneClickEditorState>(
    key: K,
    value: OneClickEditorState[K]
  ) => void;
  save: () => Promise<string>;
  discardChanges: () => void;
  reset: () => void;
}

type OneClickEditorStore = OneClickEditorStoreState & OneClickEditorActions;

// ─── Selectors ───────────────────────────────────────────────────────────────

export const selectOneClickIsDirty = (state: OneClickEditorStore) => {
  if (state.initialConfig === null) return false;
  return !isDeepEqual(state.config, state.initialConfig);
};

// ─── Store ───────────────────────────────────────────────────────────────────

const INITIAL_STATE: OneClickEditorStoreState = {
  config: DEFAULT_EDITOR_STATE,
  offerId: null,
  originalConfig: null,
  initialConfig: null,
  initialIsActive: false,
  isLoading: false,
  isSaving: false,
  error: null,
};

export const useOneClickEditorStore = create<OneClickEditorStore>()(
  devtools(
    immer((set, get) => ({
      ...INITIAL_STATE,

      initNew: () =>
        set((state) => {
          state.config = DEFAULT_EDITOR_STATE;
          state.offerId = null;
          state.error = null;
          state.isLoading = false;
          state.isSaving = false;
          state.initialConfig = DEFAULT_EDITOR_STATE;
          state.initialIsActive = false;
        }),

      loadFromBackend: async (id) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        try {
          const offer = await OfferService.get(id);
          const hydrated = offerToState(offer);
          set((state) => {
            state.config = hydrated;
            state.offerId = offer.id;
            state.originalConfig = offer.config;
            state.initialConfig = hydrated;
            state.initialIsActive = offer.is_active;
            state.isLoading = false;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error
                ? err.message
                : "No se pudo cargar el upsell";
            state.isLoading = false;
          });
          throw err;
        }
      },

      updateField: (key, value) =>
        set((state) => {
          state.config[key] = value as never;
        }),

      save: async () => {
        set((state) => {
          state.isSaving = true;
          state.error = null;
        });
        try {
          const s = get();

          let savedId: string;

          if (s.offerId === null) {
            const created = await OfferService.create(
              stateToCreateRequest(s.config)
            );
            savedId = created.id;
          } else {
            const updated = await OfferService.update(
              s.offerId,
              stateToUpdateRequest(s.config, s.originalConfig)
            );
            savedId = updated.id;
          }

          set((state) => {
            state.offerId = savedId;
            // `current()` strips the Immer draft proxy and returns a
            // plain frozen snapshot — the dirty check compares values,
            // so we need the unwrapped object, not the draft.
            state.initialConfig = current(state.config);
            state.initialIsActive = state.config.isActive;
            state.isSaving = false;
          });

          return savedId;
        } catch (err) {
          const message =
            err instanceof Error ? err.message : "Error al guardar el upsell";
          set((state) => {
            state.isSaving = false;
            state.error = message;
          });
          throw new Error(message);
        }
      },

      discardChanges: () =>
        set((state) => {
          if (!state.initialConfig) return;
          state.config = state.initialConfig;
        }),

      reset: () =>
        set((state) => {
          Object.assign(state, INITIAL_STATE);
        }),
    })),
    { name: "OneClickEditorStore" }
  )
);
